import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Sparkles, ArrowRight, Check, BarChart2, Globe, Tag, Wand2, Building2 } from 'lucide-react';
import type { DiscoveredTrend } from '../services/domainEngine';
import { getStoredBrandProfile } from '../services/openai';

interface TypeformPipelineProps {
  trends: DiscoveredTrend[];
  domainLabel: string;
  hasApiKey: boolean;
  isGenerating: boolean;
  progress: number;
  progressLabel: string;
  onGenerate: (trendId: string, tonality: string, voiceStyle: string) => void;
}

const VOICE_STYLES = ['Executive', 'Technical Expert', 'Founder Voice', 'Conversational'] as const;

const VOICE_DESCRIPTIONS: Record<string, string> = {
  'Executive': 'ROI-focused · authoritative · outcome-driven',
  'Technical Expert': 'Logic-heavy · detailed · framework-aware',
  'Founder Voice': 'Passionate · visionary · anecdotal',
  'Conversational': 'Short sentences · relatable · peer-to-peer',
};

const TONALITIES = [
  'Professional / Authoritative',
  'Conversational',
  'Informative / Educational',
  'Casual / Colloquial',
  'Assertive',
  'Humorous / Witty',
  'Inspirational / Encouraging'
];

const STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'domain', label: 'Domain' },
  { id: 'trend', label: 'Trend' },
  { id: 'confirm', label: 'Confirm' },
];

export const TypeformPipeline: React.FC<TypeformPipelineProps> = ({
  trends,
  domainLabel,
  hasApiKey,
  isGenerating,
  progress,
  progressLabel,
  onGenerate,
}) => {
  const brandProfile = getStoredBrandProfile();
  const [step, setStep] = useState(0);
  const [selectedTrendId, setSelectedTrendId] = useState(trends[0]?.id || '');
  const [tonality, setTonality] = useState(TONALITIES[0]);
  const [voiceStyle, setVoiceStyle] = useState<string>(brandProfile?.voiceStyle || VOICE_STYLES[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedTrend = trends.find((t) => t.id === selectedTrendId);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleStart = () => {
    if (!isGenerating) {
      onGenerate(selectedTrendId, tonality, voiceStyle);
    }
  };

  const slideVariants = {
    enter: { opacity: 0, y: 32 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -24 },
  };

  return (
    <div className="relative overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl">
      {/* Step progress bar */}
      <div className="flex gap-0">
        {STEPS.map((s, i) => (
          <div
            key={s.id}
            className="h-0.5 flex-1 transition-all duration-500"
            style={{ background: i <= step ? 'linear-gradient(90deg, #7c3aed, #06b6d4)' : '#1e293b' }}
          />
        ))}
      </div>

      {/* Step counter */}
      <div className="flex items-center justify-between px-3 sm:px-6 pt-4 pb-2">
        <span className="text-xs text-slate-500 font-mono">
          {step + 1} / {STEPS.length}
        </span>
        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${i === step ? 'bg-violet-400 w-4' : i < step ? 'bg-violet-600' : 'bg-slate-700'}`}
            />
          ))}
        </div>
      </div>

      {/* Main step content */}
      <div className="min-h-[260px] flex flex-col justify-center px-8 py-6">
        <AnimatePresence mode="wait">
          {/* ── STEP 0: Welcome ── */}
          {step === 0 && (
            <motion.div
              key="welcome"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 text-violet-400 text-sm font-medium">
                <Wand2 className="w-4 h-4" />
                <span>Multi-Channel Content Pipeline</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-100 leading-snug">
                Create Publish-Ready<br />
                <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                  Content Assets
                </span>
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed max-w-md">
                AI agents generate a full multi-channel bundle — Blog, Newsletter, Carousel, X Thread, IG Story, Video Workflow, Visual Prompts &amp; more.
              </p>
              {brandProfile && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-xs">
                  <Building2 className="w-3.5 h-3.5" />
                  Brand Intelligence active — <span className="font-semibold ml-1">{brandProfile.companyName}</span>
                </div>
              )}
              {!hasApiKey && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                  ⚠ No OpenAI key detected — add it in{' '}
                  <a href="#/settings" className="underline font-medium">Settings</a> first.
                </div>
              )}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={goNext}
                  className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-violet-600/20"
                >
                  Let's go <ArrowRight className="w-4 h-4" />
                </button>
                {hasApiKey && trends.length > 0 && (
                  <button
                    onClick={() => { if (!isGenerating) onGenerate(selectedTrendId, tonality, voiceStyle); }}
                    disabled={isGenerating}
                    className="flex items-center gap-2 px-5 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-50 text-slate-300 text-sm font-medium rounded-xl transition-all"
                    title="Skip setup and generate with current settings"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-violet-400" /> Quick Generate
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* ── STEP 1: Domain ── */}
          {step === 1 && (
            <motion.div
              key="domain"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
                <Globe className="w-4 h-4" />
                <span>Your domain</span>
              </div>
              <h2 className="text-xl font-bold text-slate-100">
                You're working in&nbsp;
                <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-cyan-300 font-mono">
                  {domainLabel}
                </span>
              </h2>
              <p className="text-slate-400 text-sm">
                All agents will tailor their output specifically for this domain.
                Go to <span className="text-violet-300">Trend Discovery</span> to change your domain.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
                  <BarChart2 className="w-3.5 h-3.5 text-violet-400" />
                  <span className="text-slate-300 text-xs">{trends.length} trends available</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={goBack} className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm transition-colors">
                  Back
                </button>
                <button
                  onClick={goNext}
                  className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all w-fit"
                >
                  Looks good <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Pick Trend ── */}
          {step === 2 && (
            <motion.div
              key="trend"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                <Tag className="w-4 h-4" />
                <span>Pick a trend to write about</span>
              </div>
              <h2 className="text-xl font-bold text-slate-100">Which trend signal should we use?</h2>

              {/* Custom dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-3 bg-slate-800 border border-slate-700 hover:border-violet-500/50 rounded-xl px-4 py-3 text-left transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-slate-100 font-medium text-sm truncate">{selectedTrend?.name}</p>
                    <p className="text-slate-500 text-xs truncate mt-0.5">{selectedTrend?.description}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-20 top-full mt-2 w-full bg-slate-800 border border-slate-700 rounded-xl overflow-hidden shadow-2xl shadow-black/50 max-h-56 overflow-y-auto"
                    >
                      {trends.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => { setSelectedTrendId(t.id); setDropdownOpen(false); }}
                          className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-700/60 transition-colors border-b border-slate-700/50 last:border-0 ${t.id === selectedTrendId ? 'bg-violet-500/10' : ''}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${t.id === selectedTrendId ? 'text-violet-300' : 'text-slate-200'}`}>{t.name}</p>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{t.description}</p>
                          </div>
                          {t.id === selectedTrendId && <Check className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Keyword chips */}
              {selectedTrend && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedTrend.keywords.slice(0, 5).map((kw) => (
                    <span key={kw} className="px-2 py-0.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-300 text-xs">
                      {kw}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={goBack} className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm transition-colors">
                  Back
                </button>
                <button
                  onClick={goNext}
                  disabled={!selectedTrendId}
                  className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold rounded-xl transition-all"
                >
                  Use this trend <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Confirm + Run ── */}
          {step === 3 && (
            <motion.div
              key="confirm"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Ready to generate</span>
              </div>
              <h2 className="text-xl font-bold text-slate-100">All set — launch the pipeline?</h2>

              {/* Summary card */}
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Domain</p>
                    <p className="text-slate-200 text-sm font-medium">{domainLabel}</p>
                  </div>
                </div>
                <div className="h-px bg-slate-700" />
                <div className="flex items-start gap-3">
                  <Tag className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Trend</p>
                    <p className="text-slate-200 text-sm font-medium">{selectedTrend?.name}</p>
                  </div>
                </div>
                <div className="h-px bg-slate-700" />
                <div className="flex flex-wrap gap-2">
                  {['📝 Blog','📰 Newsletter','📊 Infographic','🎬 Reel','🎨 Comic','🎠 Carousel','🧵 X Thread','📸 IG Story','🎨 Visual Prompts','🎥 Video Workflow','📄 Whitepaper'].map((a) => (
                    <span key={a} className="px-2 py-0.5 bg-slate-700 rounded-md text-slate-400 text-xs">{a}</span>
                  ))}
                </div>
              </div>

              {/* Voice + Tonality */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Voice Persona</label>
                    <select
                      value={voiceStyle}
                      onChange={(e) => setVoiceStyle(e.target.value)}
                      disabled={isGenerating}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50"
                    >
                      {VOICE_STYLES.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    {voiceStyle && <p className="text-[11px] text-slate-500 px-1">{VOICE_DESCRIPTIONS[voiceStyle]}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Tonality</label>
                    <select
                      value={tonality}
                      onChange={(e) => setTonality(e.target.value)}
                      disabled={isGenerating}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-violet-500 transition-colors disabled:opacity-50"
                    >
                      {TONALITIES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Progress bar while running */}
              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-violet-300 animate-pulse">{progressLabel}</span>
                    <span className="text-slate-500">{progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {!isGenerating && (
                  <button onClick={goBack} className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm transition-colors">
                    Back
                  </button>
                )}
                <button
                  onClick={handleStart}
                  disabled={isGenerating || !hasApiKey}
                  className="flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:opacity-60 text-white font-bold rounded-xl transition-all shadow-lg shadow-violet-600/25 flex-1 justify-center"
                >
                  {isGenerating ? (
                    <><span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Generating…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Launch Pipeline</>
                  )}
                </button>
              </div>
              {isGenerating && (
                <p className="text-xs text-slate-500 text-center">This takes ~60 seconds — all 5 agents are working in sequence.</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
